/**
 * Small spherical-earth helpers for rendering (hot paths, per-frame). Accuracy is ample for
 * display at map scales; authoritative geometry (Vincenty etc.) lives in @apos/shared.
 */
import type { LatLon } from '@apos/shared';

export const R_EARTH = 6_371_008.8;
const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;
export const KT_TO_MPS = 1852 / 3600;
export const NM = 1852;

export type LngLat = [number, number];

export function destination(lat: number, lon: number, brgDeg: number, distM: number): LatLon {
  const d = distM / R_EARTH;
  const b = brgDeg * D2R;
  const p1 = lat * D2R;
  const l1 = lon * D2R;
  const sinP2 = Math.sin(p1) * Math.cos(d) + Math.cos(p1) * Math.sin(d) * Math.cos(b);
  const p2 = Math.asin(Math.max(-1, Math.min(1, sinP2)));
  const l2 = l1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(p1), Math.cos(d) - Math.sin(p1) * sinP2);
  return { lat: p2 * R2D, lon: ((((l2 * R2D + 540) % 360) + 360) % 360) - 180 };
}

export function distance(a: LatLon, b: LatLon): number {
  const dLat = (b.lat - a.lat) * D2R;
  const dLon = (b.lon - a.lon) * D2R;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * D2R) * Math.cos(b.lat * D2R) * Math.sin(dLon / 2) ** 2;
  return 2 * R_EARTH * Math.asin(Math.min(1, Math.sqrt(s)));
}

export function bearing(a: LatLon, b: LatLon): number {
  const p1 = a.lat * D2R;
  const p2 = b.lat * D2R;
  const dl = (b.lon - a.lon) * D2R;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (Math.atan2(y, x) * R2D + 360) % 360;
}

export function midpoint(a: LatLon, b: LatLon): LatLon {
  return destination(a.lat, a.lon, bearing(a, b), distance(a, b) / 2);
}

/** Closed ring [lon,lat][] */
export function circleRing(c: LatLon, radiusM: number, segments = 64): LngLat[] {
  const out: LngLat[] = [];
  for (let i = 0; i <= segments; i++) {
    const p = destination(c.lat, c.lon, (i / segments) * 360, radiusM);
    out.push([p.lon, p.lat]);
  }
  return out;
}

/** Closed wedge ring from origin, centred on heading, spanning hfov. */
export function wedgeRing(o: LatLon, headingDeg: number, hfovDeg: number, rangeM: number, segments = 24): LngLat[] {
  const out: LngLat[] = [[o.lon, o.lat]];
  const start = headingDeg - hfovDeg / 2;
  for (let i = 0; i <= segments; i++) {
    const p = destination(o.lat, o.lon, start + (i / segments) * hfovDeg, rangeM);
    out.push([p.lon, p.lat]);
  }
  out.push([o.lon, o.lat]);
  return out;
}

/** Runway rectangle polygon between thresholds with a width in feet. */
export function runwayPolygon(le: LatLon, he: LatLon, widthFt: number): LngLat[] {
  const brg = bearing(le, he);
  const half = (widthFt * 0.3048) / 2;
  const a = destination(le.lat, le.lon, brg - 90, half);
  const b = destination(he.lat, he.lon, brg - 90, half);
  const c = destination(he.lat, he.lon, brg + 90, half);
  const d = destination(le.lat, le.lon, brg + 90, half);
  return [
    [a.lon, a.lat],
    [b.lon, b.lat],
    [c.lon, c.lat],
    [d.lon, d.lat],
    [a.lon, a.lat],
  ];
}

/** Metres per CSS pixel at a latitude/zoom for 512-px tiles (MapLibre). */
export function metersPerPixel(lat: number, zoom: number): number {
  return (40_075_016.686 * Math.cos(lat * D2R)) / (512 * 2 ** zoom);
}

/** Smallest signed angle a→b in degrees. */
export function angleDelta(a: number, b: number): number {
  return ((((b - a) % 360) + 540) % 360) - 180;
}

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
